export interface HelpSection {
  title: string;
  content: string;
  icon: string;
}

export interface HelpContext {
  title: string;
  description: string;
  sections: HelpSection[];
  quickTips: string[];
}

export const HELP_CONFIG: Record<string, HelpContext> = {
  // CREATE MATCH - Pantalla de crear partido
  createMatch: {
    title: 'Crear Partido',
    description: 'Guía paso a paso para crear un nuevo partido de fútbol',
    sections: [
      {
        title: 'Información Básica del Partido',
        content: `Para crear un partido exitoso, completa estos campos:

**Ubicación**: Nombre de la cancha (ej: "Cancha Los Pinos")
**Google Maps**: Link directo para que todos lleguen sin problemas
**Fecha**: Día del partido (idealmente 2-3 días antes)
**Hora**: Horario de inicio del partido
**Jugadores por Equipo**: 5 (fútbol 5), 7 (fútbol 7) u 11 (fútbol 11)
**Precio**: Costo por jugador (considera dividir el precio de la cancha)`,
        icon: '📝'
      },
      {
        title: 'Personalización de Equipos',
        content: `Haz tu partido más divertido personalizando los nombres:

**Equipo 1**: Cambia "Equipo 1" por nombres como "Los Tigres", "Los Leones", "Real Madrid"
**Equipo 2**: Cambia "Equipo 2" por nombres como "Los Águilas", "Los Lobos", "Barcelona"

Los nombres se muestran en la formación de equipos y hacen el partido más entretenido.`,
        icon: '🏷️'
      },
      {
        title: 'Configuración Avanzada',
        content: `Una vez creado el partido, podrás:

**Compartir Código**: Envía el código único a los jugadores
**Gestionar Jugadores**: Controla quién se une y forma los equipos
**Editar Detalles**: Modifica fecha, hora o ubicación si es necesario
**Control de Pagos**: Gestiona el estado de pagos de cada jugador`,
        icon: '⚙️'
      }
    ],
    quickTips: [
      'Usa nombres de cancha claros y reconocibles',
      'Siempre incluye link de Google Maps para que los jugadores lleguen sin problemas',
      'Crea partidos con anticipación (2-3 días)',
      'Personaliza los nombres de los equipos para que sea más divertido',
    ]
  },

  // VIEW MATCH SCHEDULED - Partido programado (organizadores)
  viewMatchScheduled: {
    title: 'Partido Programado',
    description: 'Gestiona equipos, jugadores y configuración antes del partido',
    sections: [
      {
        title: 'Organización de Equipos',
        content: `Forma y gestionar los equipos de manera inteligente:

**Drag & Drop**: Arrastra jugadores entre equipos para reorganizar
**Formación Automática**: Los equipos se forman solos al unirse jugadores
**Equilibrio por Rating**: Se mantienen balanceados por habilidad
**Nombres Personalizables**: Cambia "Equipo 1" por nombres reales

Los equipos se rebalancean automáticamente para mantener la competitividad.`,
        icon: '⚽'
      },
      {
        title: 'Acciones Disponibles',
        content: `Botones de acción para gestionar el partido:

**Importar Lista**: Carga jugadores desde listas guardadas anteriormente
**Exportar Lista**: Descarga la lista actual de jugadores
**Compartir Link**: Envía el enlace del partido a otros jugadores
**Agregar a Calendario**: Añade el partido a tu calendario personal
**Transferir Admin**: Delega la administración a otro jugador

Estas acciones te dan control total sobre la organización.`,
        icon: '🎛️'
      },
      {
        title: 'Control de Pagos',
        content: `Sistema visual para gestionar pagos de jugadores:

**Rojo**: Jugador no ha pagado
**Amarillo**: Jugador marcó como pagado (pendiente confirmación)
**Verde**: Pago confirmado por organizador

Los jugadores marcan cuando pagan, tú confirmas cuando recibes el dinero.`,
        icon: '💰'
      }
    ],
    quickTips: [
      '🔄 Usa drag & drop para reorganizar equipos',
      '📋 Importa listas de jugadores para ahorrar tiempo',
      '🔗 Comparte el link del partido fácilmente',
      '📅 Agrega el partido a tu calendario',
      '👑 Transfiere admin si no puedes asistir'
    ]
  },

  // VIEW MATCH FINISHED - Partido finalizado (reportes, tags, MVP)
  viewMatchFinished: {
    title: 'Partido Finalizado',
    description: 'Sistema de reportes, tags y votación MVP después del partido',
    sections: [
      {
        title: 'Sistema de Reportes',
        content: `Los jugadores reportan sus estadísticas del partido:

**Goles**: Cuántos goles marcó cada jugador
**Asistencias**: Cuántas asistencias dio cada jugador
**Saves**: Para arqueros, cuántos saves realizó
**Validación**: Otros jugadores confirman los reportes

Los reportes se validan entre jugadores para mantener la honestidad.`,
        icon: '📊'
      },
      {
        title: 'Tags y Comentarios',
        content: `Sistema de etiquetas para describir el partido:

**Tags del Partido**: Etiquetas que describen la experiencia (ej: "Divertido", "Competitivo")
**Comentarios**: Opiniones y feedback sobre el partido
**Valoración General**: Rating del 1 al 5 para el partido completo

Los tags ayudan a otros jugadores a entender cómo fue el partido.`,
        icon: '🏷️'
      },
      {
        title: 'Votación MVP',
        content: `Elige al jugador más valioso del partido:

**Voto Individual**: Cada jugador vota por su MVP personal
**Criterios**: Habilidad, deportividad, contribución al equipo
**Resultado**: Se calcula el MVP general del partido
**Reconocimiento**: El MVP recibe puntos extra en su perfil

La votación MVP reconoce el mejor desempeño del partido.`,
        icon: '🏆'
      }
    ],
    quickTips: [
      '📊 Reporta tus estadísticas. Esto afectara a tus stats y rating.',
      '✅ Valida los reportes de otros jugadores.',
      '🏷️ Usa tags para cada jugador. Podes crear tags personalizados!',
      '💬 Deja comentarios constructivos, o destructivos!',
      '🏆 Vota por el MVP del partido'
    ]
  },

  // JOIN MATCH - Pantalla para unirse a un partido
  joinMatch: {
    title: 'Unirse a Partido',
    description: 'Proceso completo para inscribirse en un partido existente',
    sections: [
      {
        title: 'Código del Partido',
        content: `Para unirte necesitas el código único del partido:

**Recibir Código**: El organizador te envía un código (ej: ABC123)
**Ingresar Código**: Cópialo exactamente como te lo enviaron
**Verificar Partido**: Confirma que es el partido correcto
**Verificar Disponibilidad**: Asegúrate de que haya lugar

El código es tu llave de entrada al partido.`,
        icon: '🔑'
      },
      {
        title: 'Tu Perfil de Jugador',
        content: `Completa tu información para el partido:

**Nombre**: Tu nombre completo (visible para todos)
**Posición**: Delantero, Mediocampista, Defensa o Arquero
**Rating**: Tu nivel de juego honesto (1-5 estrellas)
**Tipo**: Jugador Principal (juega seguro) o Suplente (entra si hay lugar)

Sé honesto con tu rating para equipos balanceados.`,
        icon: '👤'
      },
      {
        title: 'Posiciones Disponibles',
        content: `Elige la posición que mejor se adapte a tu juego:

**Delantero**: Enfocado en marcar goles y atacar
**Mediocampista**: Crea juego, distribuye la pelota
**Defensa**: Marca oponentes, protege el arco
**Arquero**: Ataja, organiza la defensa

Puedes cambiar de posición en futuros partidos.`,
        icon: '⚽'
      },
      {
        title: 'Confirmación y Acceso',
        content: `Una vez inscrito tendrás acceso completo:

**Ver Equipos**: Consulta tu equipo y compañeros
**Ver Ubicación**: Accede al link de Google Maps
**Ver Horario**: Confirma fecha y hora del partido
**Marcar Pago**: Confirma cuando hayas pagado
**Recibir Notificaciones**: Alertas de cambios importantes

Tu lugar está asegurado una vez confirmado.`,
        icon: '✅'
      }
    ],
    quickTips: [
      'Copia el código exactamente como te lo enviaron',
      'Sé honesto con tu rating para equipos balanceados',
      'Elige la posición donde te sientas más cómodo',
      'Los suplentes entran automáticamente si hay lugar',
      'Confirma tu inscripción para asegurar tu lugar'
    ]
  },

  // PROFILE PAGE - Pantalla de perfil del usuario
  profile: {
    title: '👤 Mi Perfil',
    description: 'Gestiona tu perfil, estadísticas y reputación en Futbolero',
    sections: [
      {
        title: '📊 Estadísticas Personales',
        content: `Tu historial completo en Futbolero:

📈 **Partidos Jugados**: Total de partidos donde participaste
⚽ **Goles Totales**: Suma de todos tus goles reportados
🎯 **Asistencias Totales**: Suma de todas tus asistencias
⭐ **Rating Promedio**: Tu calificación general en la comunidad
🏆 **Badges Obtenidos**: Logros y reconocimientos especiales

Las estadísticas se acumulan a lo largo del tiempo.`,
        icon: '📊'
      },
      {
        title: '⭐ Sistema de Rating',
        content: `Tu reputación se basa en evaluaciones de otros jugadores:

📝 **Evaluaciones Recibidas**: Reviews de cada partido
🎯 **Categorías Evaluadas**: Técnica, actitud, puntualidad
📊 **Rating por Categoría**: Puntuación específica en cada aspecto
📈 **Tendencia**: Si vas mejorando o no en el tiempo
🏅 **Posición en Ranking**: Tu lugar en la comunidad

Juega bien y sé puntual para mejorar tu rating.`,
        icon: '⭐'
      },
      {
        title: '🏆 Badges y Logros',
        content: `Gana reconocimientos por metas alcanzadas:

⚽ **Goleador**: Por marcar muchos goles
🎯 **Asistente**: Por dar muchas asistencias
🏃 **Puntual**: Por llegar siempre a tiempo
🤝 **Deportista**: Por buena actitud y espíritu deportivo
👑 **Campeón**: Por participar en muchos partidos

Los badges se otorgan automáticamente.`,
        icon: '🏆'
      },
      {
        title: '✏️ Editar Perfil',
        content: `Personaliza tu información cuando sea necesario:

📝 **Nombre**: Cambia tu nombre si es necesario
⚽ **Posición Preferida**: Actualiza tu posición favorita
⭐ **Rating**: Ajusta tu nivel de habilidad
🖼️ **Foto de Perfil**: Agrega una imagen personal
📱 **Información de Contacto**: Datos para comunicación

Los cambios se reflejan en futuros partidos.`,
        icon: '✏️'
      }
    ],
    quickTips: [
      '📊 Revisa tus estadísticas regularmente',
      '⭐ Juega bien para mejorar tu rating',
      '🏆 Los badges se ganan automáticamente',
      '✏️ Mantén tu perfil actualizado',
      '📈 Tu reputación crece con el tiempo'
    ]
  },

  // REVIEWS - Sistema de evaluaciones
  reviews: {
    title: '⭐ Sistema de Reviews',
    description: 'Cómo evaluar a otros jugadores y construir tu reputación',
    sections: [
      {
        title: '📝 Cómo Evaluar a Otros',
        content: `Después de cada partido, evalúa a otros jugadores:

⭐ **Habilidad Técnica**: Control, pase, defensa, ataque (1-5 estrellas)
🤝 **Actitud y Comportamiento**: Espíritu deportivo, comunicación, puntualidad
💬 **Comentarios Opcionales**: Opiniones específicas y constructivas
🎯 **Evaluación Objetiva**: Basa tu review en hechos, no en preferencias

Sé honesto y constructivo en tus evaluaciones.`,
        icon: '📝'
      },
      {
        title: '📊 Categorías de Evaluación',
        content: `Evalúa diferentes aspectos del juego:

⚽ **Control de Pelota**: Manejo del balón y dominio
🎯 **Pase**: Precisión y visión de juego
🛡️ **Defensa**: Marca y recuperación de pelota
⚡ **Ataque**: Definición y creación de juego
🤝 **Espíritu Deportivo**: Juego limpio y respeto
💬 **Comunicación**: Trabajo en equipo y coordinación
⏰ **Puntualidad**: Llegar a tiempo y compromiso

Cada categoría se evalúa de 1 a 5 estrellas.`,
        icon: '📊'
      },
      {
        title: '👀 Reviews Recibidas',
        content: `Consulta qué opinan de ti otros jugadores:

📈 **Rating General**: Tu promedio en todas las categorías
📊 **Análisis por Categoría**: Cómo te va en cada aspecto
📝 **Comentarios Recibidos**: Opiniones específicas de otros
🎯 **Áreas de Mejora**: Aspectos donde puedes crecer
📈 **Tendencia**: Si vas mejorando o no en el tiempo

Usa feedback negativo para identificar áreas de mejora.`,
        icon: '👀'
      },
      {
        title: '🎯 Construir Reputación',
        content: `Mejora tu rating y posición en la comunidad:

✅ **Juega Bien**: Demuestra habilidad técnica en el campo
🤝 **Sé Deportista**: Mantén buena actitud y respeto
⏰ **Llega a Tiempo**: Sé puntual y comprometido
💬 **Comunícate**: Coordina bien con tu equipo
📈 **Participa Regularmente**: Juega en muchos partidos

Tu reputación se construye con el tiempo y la consistencia.`,
        icon: '🎯'
      }
    ],
    quickTips: [
      '📝 Evalúa objetivamente basándote en hechos',
      '⭐ Sé honesto con las estrellas que das',
      '💬 Escribe comentarios constructivos',
      '👀 Usa feedback negativo para mejorar',
      '🎯 Juega bien para recibir buenas reviews'
    ]
  },

  // MY MATCHES - Historial de partidos
  myMatches: {
    title: '📅 Mis Partidos',
    description: 'Historial completo de todos los partidos donde participaste',
    sections: [
      {
        title: '📊 Vista General de Partidos',
        content: `Consulta tu historial completo de partidos:

📅 **Partidos Activos**: Donde estás inscrito y se pueden hacer cambios
🎯 **Partidos en Curso**: Donde se está jugando actualmente
✅ **Partidos Finalizados**: Terminados, solo se pueden ver resultados
📈 **Estadísticas Acumuladas**: Goles, asistencias y rating por partido

Organiza tus partidos por estado y fecha.`,
        icon: '📊'
      },
      {
        title: '⚽ Estadísticas por Partido',
        content: `Detalles específicos de cada partido:

📅 **Fecha y Hora**: Cuándo se jugó o se jugará
📍 **Ubicación**: Dónde se jugó o se jugará
👥 **Equipo**: En qué equipo jugaste o jugarás
⚽ **Goles**: Cuántos goles marcaste
🎯 **Asistencias**: Cuántas asistencias diste
⭐ **Rating**: Tu calificación en ese partido

Cada partido tiene su propia historia y estadísticas.`,
        icon: '⚽'
      },
      {
        title: '🏆 Logros y Tendencias',
        content: `Analiza tu progreso a lo largo del tiempo:

📈 **Tendencia de Rating**: Si vas mejorando o no
⚽ **Goles por Partido**: Promedio de goles marcados
🎯 **Asistencias por Partido**: Promedio de asistencias
🤝 **Participación**: Frecuencia de partidos jugados
🏅 **Posición Favorita**: Dónde juegas mejor

Identifica patrones y áreas de mejora.`,
        icon: '🏆'
      },
      {
        title: '📱 Acciones Disponibles',
        content: `Qué puedes hacer desde esta pantalla:

👀 **Ver Detalles**: Accede a la información completa del partido
📊 **Ver Reportes**: Consulta estadísticas y reviews
👥 **Ver Equipos**: Revisa la formación y compañeros
📍 **Ver Ubicación**: Accede al link de Google Maps
💬 **Ver Reviews**: Lee evaluaciones de otros jugadores

Cada partido te ofrece diferentes opciones según su estado.`,
        icon: '📱'
      }
    ],
    quickTips: [
      '📊 Revisa tu historial regularmente',
      '⚽ Analiza tus estadísticas por partido',
      '🏆 Identifica tendencias de mejora',
      '📱 Usa las acciones disponibles para cada partido',
      '📈 Tu progreso se ve reflejado en el tiempo'
    ]
  },

  // RANKINGS - Sistema de clasificación
  rankings: {
    title: '🏆 Rankings',
    description: 'Sistema de clasificación por habilidad y participación',
    sections: [
      {
        title: '📊 Cómo Funciona el Ranking',
        content: `El sistema de rankings se basa en múltiples factores:

⭐ **Rating Promedio**: Tu calificación general en la comunidad
⚽ **Goles por Partido**: Promedio de goles marcados
🎯 **Asistencias por Partido**: Promedio de asistencias
🤝 **Participación**: Frecuencia de partidos jugados
⏰ **Puntualidad**: Consistencia en llegar a tiempo
📈 **Tendencia**: Si vas mejorando o no

El ranking se actualiza después de cada partido.`,
        icon: '📊'
      },
      {
        title: '🏅 Categorías de Ranking',
        content: `Diferentes formas de clasificarte:

👑 **Ranking General**: Clasificación general por rating
⚽ **Goleadores**: Mejores anotadores de la comunidad
🎯 **Asistentes**: Mejores pasadores y creadores de juego
🤝 **Deportistas**: Mejor actitud y espíritu deportivo
⏰ **Puntuales**: Más consistentes en llegar a tiempo
📈 **En Ascenso**: Jugadores que más están mejorando

Compite en diferentes categorías para destacar.`,
        icon: '🏅'
      },
      {
        title: '📈 Progreso y Mejora',
        content: `Cómo mejorar tu posición en el ranking:

✅ **Juega Regularmente**: Participa en muchos partidos
⭐ **Recibe Buenas Reviews**: Demuestra habilidad y buena actitud
⚽ **Marca Goles**: Contribuye al éxito de tu equipo
🎯 **Da Asistencias**: Ayuda a otros a marcar
🤝 **Sé Deportista**: Mantén buena actitud y respeto
⏰ **Sé Puntual**: Llega siempre a tiempo

El ranking refleja tu consistencia y mejora.`,
        icon: '📈'
      },
      {
        title: '👥 Competencia y Motivación',
        content: `El ranking como herramienta de motivación:

🏆 **Competencia Saludable**: Compite por las mejores posiciones
📊 **Comparación Justa**: Se compara jugadores de nivel similar
🎯 **Metas Claras**: Objetivos específicos para mejorar
📈 **Progreso Visible**: Ve tu mejora a lo largo del tiempo
🤝 **Reconocimiento**: Destaca por tus logros

Usa el ranking para motivarte a mejorar.`,
        icon: '👥'
      }
    ],
    quickTips: [
      '📊 El ranking se actualiza después de cada partido',
      '🏅 Compite en diferentes categorías',
      '📈 Juega regularmente para mejorar',
      '⭐ Recibe buenas reviews para subir',
      '🎯 El ranking refleja tu consistencia'
    ]
  }
};

// Función para obtener el contexto de ayuda según la ruta o contexto directo
export const getHelpContext = (pathnameOrContext: string): HelpContext | null => {
  
  
  // Si es un contexto directo (ej: "createMatch", "viewMatchScheduled", "viewMatchFinished")
  if (HELP_CONFIG[pathnameOrContext]) {

    return HELP_CONFIG[pathnameOrContext];
  }
  
  // Si es una ruta (ej: "/create-match", "/view-match")
  if (pathnameOrContext.includes('/create-match')) return HELP_CONFIG.createMatch;
  if (pathnameOrContext.includes('/view-match') || pathnameOrContext.includes('/admin/')) return HELP_CONFIG.viewMatchScheduled;
  if (pathnameOrContext.includes('/join-match')) return HELP_CONFIG.joinMatch;
  if (pathnameOrContext.includes('/profile')) return HELP_CONFIG.profile;
  if (pathnameOrContext.includes('/reviews')) return HELP_CONFIG.reviews;
  if (pathnameOrContext.includes('/my-matches')) return HELP_CONFIG.myMatches;
  if (pathnameOrContext.includes('/rankings')) return HELP_CONFIG.rankings;
  
  
  return null;
};
